import { useState, useEffect, useCallback } from 'react';
import { PROCESS_STEPS } from '@/lib/constants';

export default function ProcessSection() {
  const [activeStep, setActiveStep] = useState(0);
  const [progress, setProgress] = useState(0);

  const goNext = useCallback(() => {
    setActiveStep(prev => (prev + 1) % PROCESS_STEPS.length);
    setProgress(0);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          goNext();
          return 0;
        }
        return prev + 2;
      });
    }, 100);
    return () => clearInterval(interval);
  }, [goNext]);

  const step = PROCESS_STEPS[activeStep];

  return (
    <section className="py-24 md:py-32 bg-white" data-testid="process-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-16">
          <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#002FA7] mb-3">How It Works</p>
          <h2 className="font-heading font-extrabold text-4xl md:text-5xl tracking-tight text-[#09090B] leading-tight">
            Three Simple Steps
          </h2>
        </div>

        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Image */}
          <div className="relative overflow-hidden" data-testid="process-image">
            <img
              src={step.image}
              alt={step.title}
              className="w-full h-[350px] object-cover transition-opacity duration-500"
              loading="lazy"
            />
          </div>

          {/* Steps */}
          <div className="space-y-0" data-testid="process-steps-list">
            {PROCESS_STEPS.map((s, idx) => (
              <button
                key={idx}
                onClick={() => { setActiveStep(idx); setProgress(0); }}
                className={`w-full text-left border-b border-zinc-200 py-5 transition-colors ${
                  idx === activeStep ? 'bg-transparent' : ''
                }`}
                data-testid={`process-step-${idx}`}
              >
                <div className="flex items-start gap-4">
                  <span className={`font-heading font-black text-2xl ${
                    idx === activeStep ? 'text-[#002FA7]' : 'text-zinc-300'
                  }`}>
                    {String(s.step).padStart(2, '0')}
                  </span>
                  <div className="flex-1">
                    <h3 className={`font-heading font-bold text-lg mb-1 ${
                      idx === activeStep ? 'text-[#09090B]' : 'text-zinc-400'
                    }`}>
                      {s.title}
                    </h3>
                    {idx === activeStep && (
                      <p className="text-sm text-zinc-600 font-body leading-relaxed mt-2 animate-fade-in">
                        {s.description}
                      </p>
                    )}
                  </div>
                </div>
                {idx === activeStep && (
                  <div className="mt-3 ml-12">
                    <div className="progress-bar">
                      <div
                        className="progress-bar-fill"
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
